import CheckIcon from "@mui/icons-material/Check";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { Box, Typography, useTheme } from "@mui/material";
import {
  BasicSwitch,
  ShowToastError,
  getStructureDataErrorKey,
  validateOverrideStructureData,
} from "@platformx/utilities";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import JSONInput from "react-json-editor-ajrm";
import locale from "react-json-editor-ajrm/locale/en";
import { useStyles } from "./StructureData.styles";

const StructureData = ({
  state,
  setState,
  structureDataEnvelope,
  openStructureData,
  copyStructureData,
  overrideMarkup = false,
  onOverrideMarkupChange,
}: any) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const theme = useTheme();
  const [overrideText, setOverrideText] = useState<string>(
    JSON.stringify(
      structureDataEnvelope?.manual ?? structureDataEnvelope?.auto ?? {},
      undefined,
      2,
    ),
  );

  useEffect(() => {
    if (overrideMarkup) {
      setOverrideText(
        JSON.stringify(
          structureDataEnvelope?.manual ?? structureDataEnvelope?.auto ?? {},
          undefined,
          2,
        ),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [overrideMarkup]);

  // Manual edits only — auto stays owned by the generator.
  const commitStructureData = (jsObject: any) => {
    setState({
      ...state,
      CommonFields: {
        ...state.CommonFields,
        structure_data: JSON.stringify({ ...structureDataEnvelope, manual: jsObject }),
      },
    });
  };

  const handleOverrideTextChange = (event) => {
    const text = event.target.value;
    setOverrideText(text);
    try {
      const parsed = JSON.parse(text);
      if (validateOverrideStructureData(parsed, false).isValid) {
        commitStructureData(parsed);
      }
    } catch {
      // Invalid while typing — kept local until it validates.
    }
  };

  const validateOverrideText = () => {
    if (overrideText.trim() === "") {
      return validateOverrideStructureData(undefined, false);
    }
    try {
      return validateOverrideStructureData(JSON.parse(overrideText), false);
    } catch (error: any) {
      return validateOverrideStructureData(undefined, true, error?.message);
    }
  };

  // Invalid override markup toasts and keeps the dialog open.
  const handleDoneClick = () => {
    if (overrideMarkup) {
      const result = validateOverrideText();
      if (!result.isValid) {
        ShowToastError(t(getStructureDataErrorKey(result.code)));
        return;
      }
    }
    openStructureData();
  };

  return (
    <Box className={classes.container}>
      <Box className={classes.containerHead}>
        <Typography variant='h4bold'>{t("page_structure_data")}</Typography>
        <Box className={classes.flexBox}>
          <Typography
            variant='p4regular'
            sx={{
              display: "flex",
              alignItems: "center",
              margin: "0 20px 0 0",
              gap: "8px",
              color: theme?.palette?.customColor?.PRIMARY || "#2d2d39",
            }}>
            {t("page_schema_markup_override")}
            <BasicSwitch
              color={theme?.palette?.customColor?.PRIMARY}
              bgcolor={theme?.palette?.customColor?.PLACEHOLDER}
              onChange={onOverrideMarkupChange}
              checked={overrideMarkup}
              ariaLabel={t("page_schema_markup_override")}
            />
          </Typography>
          <Box className={classes.copyIcon}>
            <ContentCopyIcon onClick={() => copyStructureData()} />
          </Box>
          <Box
            onClick={handleDoneClick}
            className={classes.checkIcon}
            data-testid='open-structure-data'>
            <CheckIcon />
          </Box>
        </Box>
      </Box>
      <Box className={classes.containerContent}>
        {overrideMarkup ? (
          <Box sx={{ padding: "10px 20px" }}>
            <Box
              component='textarea'
              data-testid='structure-data-override-textarea'
              value={overrideText}
              onChange={handleOverrideTextChange}
              spellCheck={false}
              sx={{
                width: "100%",
                boxSizing: "border-box",
                minHeight: "320px",
                padding: "12px",
                borderRadius: "5px",
                border: `1px solid ${theme?.palette?.customColor?.LINE || "#D9DBE9"}`,
                outline: "none",
                backgroundColor: theme?.palette?.customColor?.INPUT || "#EFF0F6",
                color: theme?.palette?.customColor?.PRIMARY || "#14142B",
                fontFamily: "'Roboto Mono', monospace",
                fontSize: "16px",
                lineHeight: 1.5,
                resize: "vertical",
                "&:focus-visible": {
                  outline: `2px solid ${theme?.palette?.customColor?.PRIMARY || "#2d2d39"}`,
                  outlineOffset: "-2px",
                },
              }}
            />
          </Box>
        ) : (
          <JSONInput
            id='json-editor'
            confirmGood={false}
            placeholder={structureDataEnvelope?.auto ?? {}}
            viewOnly
            theme='light_mitsuketa_tribute'
            locale={locale}
            height='100%'
            width='100%'
            colors={{
              string: "#1984bc",
              keys: "#000000",
              colon: "#000000",
              default: "#000000",
            }}
            style={{
              body: { fontSize: "16px", margin: "10px 20px", zIndex: 1, position: "relative" },
            }}
          />
        )}
      </Box>
    </Box>
  );
};

export default StructureData;
